import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRightIcon,
  UserGroupIcon,
  TrophyIcon,
  AcademicCapIcon,
  ChartBarIcon,
  HeartIcon,
  SparklesIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import { trainingSports } from '../data/content';
import { trainers } from '../data/trainers';
import SEO from '../components/SEO';

const Trainings: React.FC = () => {
  const [selectedSport, setSelectedSport] = useState<string | null>(null);

  const approaches = [
    {
      icon: HeartIcon,
      title: 'Positive Verstärkung',
      description: 'Wir arbeiten ausschließlich mit positiven, gewaltfreien Trainingsmethoden. Lob und Belohnung motivieren Ihren Hund und stärken die Bindung.'
    },
    {
      icon: UserGroupIcon,
      title: 'Kleine Gruppen',
      description: 'Training in kleinen Gruppen (max. 6-8 Teams) ermöglicht individuelle Betreuung und optimale Lernerfolge.'
    },
    {
      icon: ChartBarIcon,
      title: 'Individuelles Tempo',
      description: 'Jedes Team lernt in seinem eigenen Tempo. Wir passen das Training an Ihre Bedürfnisse und Ziele an.'
    },
    {
      icon: TrophyIcon,
      title: 'Wettkampfbegleitung',
      description: 'Für ambitionierte Teams bieten wir Turniervorbereitungen und Begleitung zu Wettkämpfen an.'
    }
  ];

  const getSportFeatures = (sportId: string) => {
    const features: Record<string, string[]> = {
      'obedience': [
        'Grundgehorsam & Leinenführigkeit',
        'Impulskontrolle & Frustrationstoleranz',
        'Sozialverhalten mit anderen Hunden',
        'Alltagstaugliche Übungen'
      ],
      'rally-obedience': [
        'Parcours mit bis zu 20 Stationen',
        'Kombination aus Gehorsam und Tricks',
        'Teamwork & Kommunikation',
        'Wettkampfvorbereitung möglich'
      ],
      'tournament': [
        'Vierkampf-Training',
        'Gehorsamkeit, Hürdenlauf, Slalom',
        'Hindernislauf für Fortgeschrittene',
        'Turnierbegleitung & Wettkampfvorbereitung'
      ],
      'agility': [
        'Hindernisparcours (A-Wand, Tunnel, Wippe)',
        'Geschwindigkeit & Präzision',
        'Körperliche & geistige Auslastung',
        'Vom Anfänger bis zur Turnierreife'
      ]
    };
    return features[sportId] || [];
  };

  return (
    <div className="min-h-screen bg-white dark:bg-background-dark">
      <SEO
        title="Trainingszeiten & Kurse"
        description="Hundetraining Berlin-Adlershof: Unterordnung, Rally Obedience, Turnierhundesport und Agility. Für Anfänger & Fortgeschrittene. Gästetraining samstags – aktuell nur Warteliste."
        url="/trainings"
        breadcrumbs={[
          { name: 'Startseite', url: '/' },
          { name: 'Training', url: '/trainings' }
        ]}
      />
      {/* Hero Section */}
      <section className="relative h-[400px] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/images/sports/agility/agility-course.jpg"
            alt="Agility Hundetraining Berlin - Hund beim Parcours-Training im HSV Adlershof"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/60"></div>
        </div>
        <div className="relative h-full flex items-center">
          <div className="container mx-auto px-4 max-w-5xl">
            <div className="max-w-3xl">
              <h1 className="text-2xl md:text-5xl font-black text-white mb-4 md:mb-6 uppercase">
                Unsere Trainings & Sportangebote
              </h1>
              <p className="text-base md:text-xl text-white/90">
                Von der spielerischen Grunderziehung bis zum wettkampforientierten Sport - 
                bei uns findet jedes Mensch-Hund-Team das passende Angebot.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Training Sections with Alternating Layout */}
      <section className="py-10 md:py-20 bg-neutral-50 dark:bg-background-dark">
        <div className="container-custom space-y-12 md:space-y-24">
          {/* Unterordnung Section - Text Left, Image Right */}
          <div className="grid lg:grid-cols-2 gap-8 md:gap-16 items-center">
            <div className="space-y-6">
              <div className="inline-block w-16 h-1 bg-primary-600 dark:bg-primary-400"></div>
              <h2 className="text-3xl md:text-4xl font-black text-neutral-900 dark:text-neutral-100 uppercase">
                Unterordnung
              </h2>
              <p className="text-base text-neutral-600 dark:text-neutral-400 leading-relaxed">
                Unser Unterordnungstraining fördert die Gehorsamkeit und Teamarbeit zwischen Mensch und Hund. 
                Es werden grundlegende Kommandos gelehrt, die die Kommunikation und Bindung verbessern.
              </p>
              <div className="space-y-3">
                <div className="flex items-center">
                  <ArrowRightIcon className="w-4 h-4 text-neutral-900 dark:text-neutral-100 mr-3 flex-shrink-0" />
                  <span className="text-sm text-neutral-900 dark:text-neutral-100 font-bold uppercase tracking-wider">
                    Fördert Gehorsam und Teamarbeit
                  </span>
                </div>
                <div className="flex items-center">
                  <ArrowRightIcon className="w-4 h-4 text-neutral-900 dark:text-neutral-100 mr-3 flex-shrink-0" />
                  <span className="text-sm text-neutral-900 dark:text-neutral-100 font-bold uppercase tracking-wider">
                    Geeignet für alle Rassen
                  </span>
                </div>
                <div className="flex items-center">
                  <ArrowRightIcon className="w-4 h-4 text-neutral-900 dark:text-neutral-100 mr-3 flex-shrink-0" />
                  <span className="text-sm text-neutral-900 dark:text-neutral-100 font-bold uppercase tracking-wider">
                    Erfahrene Trainer an Ihrer Seite
                  </span>
                </div>
              </div>
              <Link 
                to="/sport/unterordnung"
                className="inline-block mt-6 bg-primary-600 text-white px-6 py-2.5 font-bold hover:bg-primary-700 transition-colors uppercase tracking-wider text-sm"
              >
                Mehr erfahren
              </Link>
            </div>
            <div className="relative h-[250px] md:h-[400px] overflow-hidden bg-neutral-100 dark:bg-neutral-800 rounded-lg">
              <img
                src="/images/sports/obedience/obedience-training.jpg"
                alt="Unterordnungstraining Berlin - Hund und Hundeführer beim Gehorsamstraining HSV Adlershof"
                className="w-full h-full object-contain"
              />
            </div>
          </div>

          {/* Rally Obedience Section - Image Left, Text Right */}
          <div className="grid lg:grid-cols-2 gap-8 md:gap-16 items-center">
            <div className="relative h-[250px] md:h-[400px] overflow-hidden bg-neutral-100 dark:bg-neutral-800 rounded-lg">
              <img
                src="/images/sports/rally-obedience/rally-demo.jpg"
                alt="Rally Obedience Training Berlin - Hund und Halter beim Parcours mit Gehorsamkeitsübungen"
                className="w-full h-full object-contain"
              />
            </div>
            <div className="space-y-6">
              <div className="inline-block w-16 h-1 bg-primary-600 dark:bg-primary-400"></div>
              <h2 className="text-3xl md:text-4xl font-black text-neutral-900 dark:text-neutral-100 uppercase">
                Rally Obedience
              </h2>
              <p className="text-base text-neutral-600 dark:text-neutral-400 leading-relaxed">
                Die Obedience Rallye kombiniert Gehorsamkeitsübungen mit einem Parcours. 
                Diese spannende Disziplin fördert die Zusammenarbeit und schafft Vertrauen zwischen Mensch und Hund.
              </p>
              <div className="space-y-3">
                <div className="flex items-center">
                  <ArrowRightIcon className="w-4 h-4 text-neutral-900 dark:text-neutral-100 mr-3 flex-shrink-0" />
                  <span className="text-sm text-neutral-900 dark:text-neutral-100 font-bold uppercase tracking-wider">
                    Parcours mit bis zu 20 Stationen
                  </span>
                </div>
                <div className="flex items-center">
                  <ArrowRightIcon className="w-4 h-4 text-neutral-900 dark:text-neutral-100 mr-3 flex-shrink-0" />
                  <span className="text-sm text-neutral-900 dark:text-neutral-100 font-bold uppercase tracking-wider">
                    Stärkt die Mensch-Hund-Bindung
                  </span>
                </div>
                <div className="flex items-center">
                  <ArrowRightIcon className="w-4 h-4 text-neutral-900 dark:text-neutral-100 mr-3 flex-shrink-0" />
                  <span className="text-sm text-neutral-900 dark:text-neutral-100 font-bold uppercase tracking-wider">
                    Wettkampfvorbereitung möglich
                  </span>
                </div>
              </div>
              <Link 
                to="/sport/rally-obedience"
                className="inline-block mt-6 bg-primary-600 text-white px-6 py-2.5 font-bold hover:bg-primary-700 transition-colors uppercase tracking-wider text-sm"
              >
                Mehr erfahren
              </Link>
            </div>
          </div>

          {/* Agility Section - Text Left, Image Right */}
          <div className="grid lg:grid-cols-2 gap-8 md:gap-16 items-center">
            <div className="space-y-6">
              <div className="inline-block w-16 h-1 bg-primary-600 dark:bg-primary-400"></div>
              <h2 className="text-3xl md:text-4xl font-black text-neutral-900 dark:text-neutral-100 uppercase">
                Agility Training
              </h2>
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-3 mb-4">
                <p className="text-sm text-amber-900 dark:text-amber-200 font-medium">
                  ℹ️ Agility-Training pausiert momentan
                </p>
              </div>
              <p className="text-base text-neutral-600 dark:text-neutral-400 leading-relaxed">
                Agility Training ist ein dynamischer Sport, bei dem Hunde durch einen Parcours aus Hürden, Tunnel und anderen Hindernissen
                geführt werden. Dies fördert sowohl Fitness als auch Konzentration.
              </p>
              <div className="space-y-3">
                <div className="flex items-center">
                  <ArrowRightIcon className="w-4 h-4 text-neutral-900 dark:text-neutral-100 mr-3 flex-shrink-0" />
                  <span className="text-sm text-neutral-900 dark:text-neutral-100 font-bold uppercase tracking-wider">
                    Fördert schnelle Beweglichkeit
                  </span>
                </div>
                <div className="flex items-center">
                  <ArrowRightIcon className="w-4 h-4 text-neutral-900 dark:text-neutral-100 mr-3 flex-shrink-0" />
                  <span className="text-sm text-neutral-900 dark:text-neutral-100 font-bold uppercase tracking-wider">
                    Spaßiger Sport für aktive Hunde
                  </span>
                </div>
                <div className="flex items-center">
                  <ArrowRightIcon className="w-4 h-4 text-neutral-900 dark:text-neutral-100 mr-3 flex-shrink-0" />
                  <span className="text-sm text-neutral-900 dark:text-neutral-100 font-bold uppercase tracking-wider">
                    Optimale Fitness für Hund und Halter
                  </span>
                </div>
              </div>
              <Link 
                to="/sport/agility"
                className="inline-block mt-6 bg-primary-600 text-white px-6 py-2.5 font-bold hover:bg-primary-700 transition-colors uppercase tracking-wider text-sm"
              >
                Mehr erfahren
              </Link>
            </div>
            <div className="relative h-[250px] md:h-[400px] overflow-hidden bg-neutral-100 dark:bg-neutral-800 rounded-lg">
              <img
                src="/images/sports/agility/agility-course.jpg"
                alt="Agility Hindernisparcours - Hund bei Geschicklichkeitstraining mit Hürden und Tunneln Berlin"
                className="w-full h-full object-contain"
              />
            </div>
          </div>

          {/* Turnierhundesport Section - Image Left, Text Right */}
          <div className="grid lg:grid-cols-2 gap-8 md:gap-16 items-center">
            <div className="relative h-[250px] md:h-[400px] overflow-hidden bg-neutral-100 dark:bg-neutral-800 rounded-lg">
              <img
                src="/images/sports/ths/ths-showcase.jpg"
                alt="Turnierhundesport Training Berlin - Vierkampf Training mit Gehorsam und Hürdenlauf HSV Adlershof"
                className="w-full h-full object-contain"
              />
            </div>
            <div className="space-y-6">
              <div className="inline-block w-16 h-1 bg-primary-600 dark:bg-primary-400"></div>
              <h2 className="text-3xl md:text-4xl font-black text-neutral-900 dark:text-neutral-100 uppercase">
                Turnierhunde Sport
              </h2>
              <p className="text-base text-neutral-600 dark:text-neutral-400 leading-relaxed">
                Im Turnierhundesport treten Mensch-Hund-Teams in verschiedenen Disziplinen gegeneinander an. Hierbei stehen 
                Geschicklichkeit und Präzision im Vordergrund, was die Bindung zwischen Mensch und Hund stärkt.
              </p>
              <div className="space-y-3">
                <div className="flex items-center">
                  <ArrowRightIcon className="w-4 h-4 text-neutral-900 dark:text-neutral-100 mr-3 flex-shrink-0" />
                  <span className="text-sm text-neutral-900 dark:text-neutral-100 font-bold uppercase tracking-wider">
                    Wettbewerbsorientiertes Training
                  </span>
                </div>
                <div className="flex items-center">
                  <ArrowRightIcon className="w-4 h-4 text-neutral-900 dark:text-neutral-100 mr-3 flex-shrink-0" />
                  <span className="text-sm text-neutral-900 dark:text-neutral-100 font-bold uppercase tracking-wider">
                    Steigerung von Geschicklichkeit und Tempo
                  </span>
                </div>
                <div className="flex items-center">
                  <ArrowRightIcon className="w-4 h-4 text-neutral-900 dark:text-neutral-100 mr-3 flex-shrink-0" />
                  <span className="text-sm text-neutral-900 dark:text-neutral-100 font-bold uppercase tracking-wider">
                    Inspiration durch Wettkämpfe
                  </span>
                </div>
              </div>
              <Link 
                to="/sport/turnierhundesport"
                className="inline-block mt-6 bg-primary-600 text-white px-6 py-2.5 font-bold hover:bg-primary-700 transition-colors uppercase tracking-wider text-sm"
              >
                Mehr erfahren
              </Link>
            </div>
          </div>
        </div>
      </section>


      {/* Trainers */}
      <section className="section">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-neutral-900 dark:text-neutral-100">
              Unser Trainer-Team
            </h2>
            <p className="text-lg text-neutral-600 dark:text-neutral-400 max-w-3xl mx-auto">
              Lernen Sie unsere qualifizierten und engagierten Trainer kennen. Mit langjähriger Erfahrung 
              und viel Herzblut unterstützen sie Sie und Ihren Hund auf Ihrem gemeinsamen Weg.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {trainers.map((trainer, index) => (
              <div key={trainer.id} className="card overflow-hidden hover:shadow-xl transition-all duration-300 group">
                <div className="h-48 md:h-64 overflow-hidden bg-primary-50 dark:bg-primary-900/20">
                  {trainer.image ? (
                    <img 
                      src={trainer.image} 
                      alt={trainer.name}
                      className="w-full h-full object-cover object-center hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <UserIcon className="w-20 h-20 text-primary-400 dark:text-primary-600" />
                    </div>
                  )}
                </div>
                <div className="p-4 md:p-6 space-y-2 md:space-y-3">
                  <div>
                    <h3 className="text-xl font-bold text-neutral-900 dark:text-neutral-100">
                      {trainer.name}
                    </h3>
                    <p className="text-primary-600 dark:text-primary-400 font-medium mt-1">
                      {trainer.role}
                    </p>
                  </div>
                  <p className="text-sm text-neutral-500 dark:text-neutral-400">
                    {trainer.experience}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {trainer.specialization.map((spec, idx) => (
                      <span 
                        key={idx}
                        className="px-3 py-1 bg-primary-100 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 text-xs font-medium rounded-full"
                      >
                        {spec}
                      </span>
                    ))}
                  </div>
                  {trainer.description && (
                    <p className="text-sm text-neutral-600 dark:text-neutral-400">
                      {trainer.description}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Training Approach */}
      <section className="section bg-primary-50 dark:bg-primary-900/10">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-neutral-900 dark:text-neutral-100">
              Unser Trainingsansatz
            </h2>
            <p className="text-lg text-neutral-600 dark:text-neutral-400">
              Moderne, wissenschaftlich fundierte Methoden für nachhaltigen Erfolg
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {approaches.map((approach, index) => {
              const Icon = approach.icon;
              return (
                <div key={index} className="text-center">
                  <div className="w-16 h-16 bg-white dark:bg-surface-dark rounded-2xl shadow-lg flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-8 h-8 text-primary-600 dark:text-primary-400" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3 text-neutral-900 dark:text-neutral-100">
                    {approach.title}
                  </h3>
                  <p className="text-neutral-600 dark:text-neutral-400">
                    {approach.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section bg-primary-600 text-white">
        <div className="container mx-auto px-4 max-w-5xl text-center">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Bereit für den ersten Schritt?
            </h2>
            <p className="text-xl mb-8 max-w-3xl mx-auto opacity-95">
              Unsere Samstagsgruppe ist aktuell ausgebucht – trag dich gerne über das Kontaktformular auf die Warteliste ein,
              wir melden uns bei dir, sobald ein Platz frei wird.
            </p>
            <Link
              to="/kontakt"
              className="inline-flex items-center bg-white text-primary-600 px-8 py-4 rounded-lg font-semibold hover:bg-neutral-100 transition-colors"
            >
              Auf Warteliste setzen
              <ArrowRightIcon className="w-5 h-5 ml-2" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Trainings;